import React, { useState, useEffect, useRef } from "react";
import { getISTDateInputValue, getISTTimeParts } from "../utils/dateTime";

const createThermalLogoDataUrl = (imageUrl) => new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => {
        try {
            const scale = Math.min(1, 384 / image.naturalWidth);
            const width = Math.max(1, Math.round(image.naturalWidth * scale));
            const height = Math.max(1, Math.round(image.naturalHeight * scale));
            const canvas = document.createElement("canvas");
            canvas.width = width;
            canvas.height = height;

            const context = canvas.getContext("2d");
            if (!context) {
                reject(new Error("Could not prepare the clinic logo for thermal printing."));
                return;
            }

            context.fillStyle = "#fff";
            context.fillRect(0, 0, width, height);
            context.drawImage(image, 0, 0, width, height);

            const imageData = context.getImageData(0, 0, width, height);
            for (let i = 0; i < imageData.data.length; i += 4) {
                const luminance =
                    0.299 * imageData.data[i] +
                    0.587 * imageData.data[i + 1] +
                    0.114 * imageData.data[i + 2];
                const color = luminance < 185 ? 0 : 255;
                imageData.data[i] = color;
                imageData.data[i + 1] = color;
                imageData.data[i + 2] = color;
                imageData.data[i + 3] = 255;
            }
            context.putImageData(imageData, 0, 0);

            resolve(canvas.toDataURL("image/png"));
        } catch (error) {
            reject(error);
        }
    };
    image.onerror = () => reject(new Error("Could not load the clinic logo for thermal printing."));
    image.src = imageUrl;
});

function BillingModal({ show, onClose, clinicName, onAddNewProduct, clinic }) {

    const generateReceiptNo = () => {
        const now = new Date();
        const pad = (n) => n.toString().padStart(2, "0");
        const date = getISTDateInputValue(now).replace(/-/g, "");
        const { hour, minute, second } = getISTTimeParts(now);
        const time = `${pad(hour)}${pad(minute)}${pad(second)}`;

        return `RCPT-${date}-${time}`;
    };

    const [barcode, setBarcode] = useState("");
    const [searchSuggestions, setSearchSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [suggestionsLoading, setSuggestionsLoading] = useState(false);
    const [useLabTestSearch, setUseLabTestSearch] = useState(false);
    const [labTests, setLabTests] = useState([]);
    const [labTestLoading, setLabTestLoading] = useState(false);
    const [productName, setProductName] = useState("");
    const [cart, setCart] = useState([]);
    const [total, setTotal] = useState(0);
    const [batches, setBatches] = useState([]);
    const [currentBarcode, setCurrentBarcode] = useState("");
    const [gstRate, setGstRate] = useState(0); // ✅ used correctly now
    const [pendingBarcode, setPendingBarcode] = useState("");
    const barcodeRef = useRef(null);
    const [showPrint, setShowPrint] = useState(false);
    const [showManualEntry, setShowManualEntry] = useState(false);
    const [showAddToBillPrompt, setShowAddToBillPrompt] = useState(false);
    const [receiptNo, setReceiptNo] = useState(generateReceiptNo());
    const [message, setMessage] = useState(null);
    const [messageType, setMessageType] = useState(""); // success | danger
    const suggestionsTimer = useRef(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const labTestsLoadedRef = useRef(false);
    const setManualEntryCleanup = () => {
        setShowManualEntry(false);
        setShowAddToBillPrompt(false);
        setPendingBarcode("");

        setManualItem({
            name: "",
            barcode: "",
            quantity: "",
            price: "",
            gst: ""
        });

        setBarcode("");

        setTimeout(() => barcodeRef.current?.focus(), 0);
    };

    const [manualItem, setManualItem] = useState({
        name: "",
        barcode: "",
        quantity: "",
        price: "",
        gst: "",
        discount: ""
    });

    useEffect(() => {
        const t = cart.reduce((sum, item) => sum + item.total, 0);
        setTotal(t);
    }, [cart]);

    useEffect(() => {
        if (show) {
            setBarcode("");
            setSearchSuggestions([]);
            setShowSuggestions(false);
            setSuggestionsLoading(false);
            setUseLabTestSearch(false);
            setLabTests([]);
            setLabTestLoading(false);
            labTestsLoadedRef.current = false;
            setCart([]);
            setMessage(null);
            setMessageType("");
            setTotal(0);
            setBatches([]);
            setShowPrint(false);
            setShowAddToBillPrompt(false);
            setCurrentBarcode("");
            setGstRate(0);
            setPendingBarcode("");
            setShowManualEntry(false);
        }
    }, [show]);

    useEffect(() => {
        window.clearTimeout(suggestionsTimer.current);

        const query = String(barcode || "").trim();
        const minSearchLength = useLabTestSearch ? 1 : 2;

        if (!show || query.length < minSearchLength || showManualEntry || showAddToBillPrompt) {
            setSearchSuggestions([]);
            setShowSuggestions(false);
            setSuggestionsLoading(false);
            return undefined;
        }

        if (useLabTestSearch) {
            if (labTests.length === 0) {
                setSearchSuggestions([]);
                setShowSuggestions(true);
                setSuggestionsLoading(labTestLoading);
                return undefined;
            }

            const normalizedQuery = query.toLowerCase();
            const filtered = labTests.filter((test) => {
                const name = String(test?.profileParticulars || test?.testName || "").toLowerCase();
                const category = String(test?.category || "").toLowerCase();
                const machine = String(test?.machineType || "").toLowerCase();
                const specimen = String(test?.specimenRequired || "").toLowerCase();
                const testId = String(test?.id || "").toLowerCase();

                return (
                    name.includes(normalizedQuery) ||
                    category.includes(normalizedQuery) ||
                    machine.includes(normalizedQuery) ||
                    specimen.includes(normalizedQuery) ||
                    testId.includes(normalizedQuery)
                );
            });

            setSearchSuggestions(filtered.slice(0, 12));
            setShowSuggestions(filtered.length > 0);
            setSuggestionsLoading(false);
            return undefined;
        }

        suggestionsTimer.current = window.setTimeout(async () => {
            try {
                setSuggestionsLoading(true);

                const token = localStorage.getItem("token");
                const res = await fetch(
                    `/v1/stocks/search?keyword=${encodeURIComponent(query)}`,
                    {
                        headers: token ? { Authorization: "Bearer " + token } : {}
                    }
                );

                if (!res.ok) {
                    throw new Error("Failed to load suggestions");
                }

                const data = await res.json();
                const list = Array.isArray(data)
                    ? data
                    : Array.isArray(data?.data)
                        ? data.data
                        : Array.isArray(data?.content)
                            ? data.content
                            : Array.isArray(data?.items)
                                ? data.items
                                : [];

                setSearchSuggestions(list);
                setShowSuggestions(list.length > 0);
            } catch (err) {
                console.error(err);
                setSearchSuggestions([]);
                setShowSuggestions(false);
            } finally {
                setSuggestionsLoading(false);
            }
        }, 300);

        return () => window.clearTimeout(suggestionsTimer.current);
    }, [barcode, show, showManualEntry, showAddToBillPrompt, useLabTestSearch, labTests, labTestLoading]);

    useEffect(() => {
        if (!show || !useLabTestSearch || labTestsLoadedRef.current) return;

        const loadLabTests = async () => {
            try {
                setLabTestLoading(true);

                const token = localStorage.getItem("token");
                const res = await fetch("/v1/labTest/clinic", {
                    headers: { Authorization: "Bearer " + token }
                });

                if (!res.ok) {
                    throw new Error("Failed to load lab tests");
                }

                const data = await res.json();
                setLabTests(Array.isArray(data) ? data : []);
            } catch (err) {
                console.error(err);
                setLabTests([]);
            } finally {
                setLabTestLoading(false);
                labTestsLoadedRef.current = true;
            }
        };

        loadLabTests();
    }, [show, useLabTestSearch]);

    if (!show) return null;

    const calculateGST = (inclusiveAmount, gstRate) => {
        const amount = Number(inclusiveAmount) || 0;
        const rate = Number(gstRate) || 0;

        if (rate <= 0) {
            return {
                basePrice: amount,
                gstAmount: 0,
                cgst: 0,
                sgst: 0
            };
        }

        const base = amount / (1 + rate / 100);
        const gst = amount - base;

        return {
            basePrice: base,
            gstAmount: gst,
            cgst: gst / 2,
            sgst: gst / 2
        };
    };

    const isServiceItem = (item) =>
        String(item?.category || "").toLowerCase() === "services";

    const normalizeBarcode = (value) =>
        String(value || "").trim().toUpperCase();

    const handleBarcodeChange = (value) => {
        setBarcode(value);
        setShowSuggestions(true);
    };

    const addLabTestToCart = (test) => {
        const labTestName = test?.profileParticulars || test?.testName || "Lab Test";
        const labTestBarcode = normalizeBarcode(`LABTEST-${test?.id || labTestName}`);
        const labTestPrice = Number(test?.actualPrice ?? test?.price ?? 0) || 0;

        addToCart(
            {
                batchNumber: `LABTEST-${test?.id || labTestBarcode}`,
                price: labTestPrice,
                category: "services"
            },
            labTestName,
            labTestBarcode,
            0,
            0
        );

        setShowSuggestions(false);
        setSearchSuggestions([]);
        setBarcode("");
        setTimeout(() => barcodeRef.current?.focus(), 0);
    };

    const selectSuggestion = (item) => {
        if (useLabTestSearch) {
            addLabTestToCart(item);
            return;
        }

        const suggestedValue = normalizeBarcode(
            item?.barcode || item?.code || item?.productCode || item?.profileParticulars || item?.productName
        );

        setShowSuggestions(false);
        setSearchSuggestions([]);
        setBarcode(suggestedValue);
        setTimeout(() => {
            barcodeRef.current?.focus();
            fetchAndAddProduct(suggestedValue);
        }, 0);
    };

    const taxableItems = cart.filter((item) => !isServiceItem(item));
    const serviceItems = cart.filter((item) => isServiceItem(item));

    const fetchAndAddProduct = async (inputValue = barcode) => {
        const scannedBarcode = normalizeBarcode(inputValue);

        // ✅ NEW: If already in cart → increase qty (works for both DB + manual items)
        const existing = cart.find(item => normalizeBarcode(item.barcode) === scannedBarcode);

        if (existing) {
            setCart(prev =>
                prev.map(item =>
                    normalizeBarcode(item.barcode) === scannedBarcode
                        ? {
                            ...item,
                            quantity: item.quantity + 1,
                            total: (item.quantity + 1) * (item.price - (item.price * (item.itemDiscount || 0) / 100))
                        }
                        : item
                )
            );

            setBarcode("");
            setTimeout(() => barcodeRef.current?.focus(), 0);
            return; // 🚀 stop further execution (VERY IMPORTANT)
        }

        try {
            const token = localStorage.getItem("token");

            const res = await fetch(
                `/v1/stocks/product/${scannedBarcode}`,
                {
                    headers: { Authorization: "Bearer " + token }
                }
            );

            const data = await res.json();

            if (!data.productExists) {
                setPendingBarcode(scannedBarcode);
                setShowManualEntry(false);
                setShowAddToBillPrompt(true);
                setMessage(null);
                setMessageType("");
                return;
            }

            setCurrentBarcode(data.barcode);
            setGstRate(data.gstRate); // ✅ IMPORTANT FIX

            if (data.batches?.length > 1) {
                setBatches(data.batches);
                setProductName(data.productName);
            } else if (data.batches?.length === 1) {
                addToCart(
                    data.batches[0],
                    data.productName,
                    data.barcode,
                    data.gstRate,
                    data.batches[0].itemDiscount || 0
                );
            }

            setBarcode("");

        } catch (err) {
            console.error(err);
        }
    };



    const updateDiscount = (index, value) => {
        const discount = parseFloat(value) || 0;

        setCart(prev => {
            const updated = [...prev];
            const item = updated[index];

            item.itemDiscount = discount;

            const discountedPrice =
                item.price - (item.price * discount / 100);

            if (isServiceItem(item)) {
                item.basePrice = discountedPrice;
                item.gstPerUnit = 0;
                item.cgstPerUnit = 0;
                item.sgstPerUnit = 0;
            } else {
                const gstData = calculateGST(discountedPrice, item.gstRate);
                item.basePrice = gstData.basePrice;
                item.gstPerUnit = gstData.gstAmount;
                item.cgstPerUnit = gstData.cgst;
                item.sgstPerUnit = gstData.sgst;
            }

            item.discountedPrice = discountedPrice;
            item.total = item.quantity * discountedPrice;

            return updated;
        });
    };

    const updatePrice = (index, value) => {
        const price = parseFloat(value) || 0;

        setCart(prev => {
            const updated = [...prev];
            const item = updated[index];

            item.price = price;

            if (isServiceItem(item)) {
                item.basePrice = price;
                item.gstPerUnit = 0;
                item.cgstPerUnit = 0;
                item.sgstPerUnit = 0;
            } else {
                const discountedPrice =
                    price - (price * (item.itemDiscount || 0) / 100);

                const gstData = calculateGST(discountedPrice, item.gstRate);

                item.basePrice = gstData.basePrice;
                item.gstPerUnit = gstData.gstAmount;
                item.cgstPerUnit = gstData.cgst;
                item.sgstPerUnit = gstData.sgst;
            }

            const discountedPrice =
                price - (price * (item.itemDiscount || 0) / 100);

            item.discountedPrice = discountedPrice;
            item.total = item.quantity * discountedPrice;

            return updated;
        });
    };

    const addToCart = (batch, productName, barcode, gstRate, itemDiscount = 0) => {
        barcode = normalizeBarcode(barcode);
        const category = batch.category || "products";
        const isService = String(category).toLowerCase() === "services";
        const effectiveGstRate = isService ? 0 : gstRate;

        const discountAmount = (batch.price * itemDiscount) / 100;
        const finalPrice = batch.price - discountAmount;
        const gstData = isService
            ? { basePrice: finalPrice, gstAmount: 0, cgst: 0, sgst: 0 }
            : calculateGST(finalPrice, effectiveGstRate);

        const existing = cart.find(
            c => c.batchNumber === batch.batchNumber && normalizeBarcode(c.barcode) === barcode
        );

        if (existing) {
            setCart(
                cart.map(c =>
                    c.batchNumber === batch.batchNumber && normalizeBarcode(c.barcode) === barcode
                        ? {
                            ...c,
                            quantity: c.quantity + 1,
                            total: (c.quantity + 1) * (c.price - (c.price * c.itemDiscount / 100))
                        }
                        : c
                )
            );
        } else {
            setCart([
                ...cart,
                {
                    batchNumber: batch.batchNumber,
                    productName,
                    barcode,
                    category,
                    price: batch.price,
                    itemDiscount,
                    discountedPrice: finalPrice,
                    quantity: 1,
                    total: finalPrice,

                    // ✅ STORE PER UNIT GST (IMPORTANT FIX)
                    gstRate: effectiveGstRate,
                    basePrice: gstData.basePrice,
                    gstPerUnit: gstData.gstAmount,
                    cgstPerUnit: gstData.cgst,
                    sgstPerUnit: gstData.sgst
                }
            ]);
        }

        setBatches([]);
        setBarcode("");

        setTimeout(() => {
            barcodeRef.current?.focus();
        }, 0);
    };

    const updateQuantity = (index, value) => {
        const qty = parseInt(value);
        if (isNaN(qty) || qty < 1) return;

        const updatedCart = [...cart];
        const item = updatedCart[index];

        const discountedPrice =
            item.price - (item.price * (item.itemDiscount || 0) / 100);

        item.quantity = qty;
        item.total = qty * discountedPrice;

        setCart(updatedCart);
    };

    const removeItem = (indexToRemove) => {
        const updatedCart = cart.filter(
            (_, index) => index !== indexToRemove
        );

        setCart(updatedCart);
        setTimeout(() => barcodeRef.current?.focus(), 0);
    };

    const handleAddManualItem = () => {
        if (!manualItem.name || !manualItem.price || !manualItem.quantity) {
            setMessage("Please fill required fields");
            setMessageType("error")
            return;
        }

        const qty = parseInt(manualItem.quantity);
        const price = parseFloat(manualItem.price);
        const gst = parseFloat(manualItem.gst || 0);
        const itemDiscount = parseFloat(manualItem.discount || 0);

        // ✅ Correct discount calculation
        const discountedPricePerUnit = price - (price * itemDiscount / 100);
        const gstData = calculateGST(discountedPricePerUnit, gst);
        const total = qty * discountedPricePerUnit;

        const manualBarcode = normalizeBarcode(manualItem.barcode);
        const existing = cart.find(item => normalizeBarcode(item.barcode) === manualBarcode);

        if (existing) {
            setCart(prev =>
                prev.map(item =>
                    normalizeBarcode(item.barcode) === manualBarcode
                        ? {
                            ...item,
                            quantity: item.quantity + qty,
                            total: (item.quantity + qty) * discountedPricePerUnit
                        }
                        : item
                )
            );
        } else {
            setCart(prev => [
                ...prev,
                {
                    batchNumber: "MANUAL",
                    productName: manualItem.name,
                    barcode: manualBarcode,
                    category: "products",
                    price: price,
                    itemDiscount,
                    discountedPrice: discountedPricePerUnit,
                    quantity: qty,
                    total: total,

                    gstRate: gst,
                    basePrice: gstData.basePrice,
                    gstPerUnit: gstData.gstAmount,
                    cgstPerUnit: gstData.cgst,
                    sgstPerUnit: gstData.sgst
                }
            ]);
        }

        setManualEntryCleanup();
    };

    const handleAddUnknownProductToBill = () => {
        const barcodeToAdd = pendingBarcode || normalizeBarcode(barcode);

        setShowAddToBillPrompt(false);
        setMessage(null);
        setMessageType("");
        setManualItem({
            name: "",
            barcode: barcodeToAdd,
            quantity: "",
            price: "",
            gst: "",
            discount: ""
        });
        setShowManualEntry(true);

        setTimeout(() => barcodeRef.current?.focus(), 0);
    };

    const handleCancelUnknownProduct = () => {
        setShowAddToBillPrompt(false);
        setPendingBarcode("");
        setMessage(null);
        setMessageType("");
        setBarcode("");

        setTimeout(() => barcodeRef.current?.focus(), 0);
    };

    const handlePrintNow = async () => {
        const receipt = document.getElementById("print-section");
        if (!receipt) {
            setMessage("Could not find the receipt to print.");
            setMessageType("danger");
            return;
        }

        const printReceipt = receipt.cloneNode(true);
        const logo = printReceipt.querySelector(".receipt-logo");
        if (logo) {
            try {
                logo.src = await createThermalLogoDataUrl(logo.currentSrc || logo.src);
                await logo.decode();
            } catch (error) {
                console.error("Could not optimize the clinic logo for thermal printing.", error);
            }
        }

        printReceipt.removeAttribute("id");
        printReceipt.classList.add("receipt-printing");
        document.body.appendChild(printReceipt);

        let cleanupTimer;
        const cleanupPrintReceipt = () => {
            printReceipt.remove();
            window.removeEventListener("afterprint", cleanupPrintReceipt);
            window.clearTimeout(cleanupTimer);
        };
        window.addEventListener("afterprint", cleanupPrintReceipt);

        try {
            window.print();
        } finally {
            cleanupTimer = window.setTimeout(cleanupPrintReceipt, 60000);
        }
    };

    const handleSubmit = async () => {
        if (isSubmitting) {
            return;
        }
        if (cart.length === 0) {
            setMessage("Cart is empty");
            setMessageType("danger");
            return;
        }

        setIsSubmitting(true);

        try {
            const token = localStorage.getItem("token");

            // ✅ Build items payload
            const items = cart.map(item => {
                const discountAmountPerUnit =
                    (item.price * (item.itemDiscount || 0)) / 100;

                const finalValuePerUnit =
                    item.price - discountAmountPerUnit;

                return {
                    stockId: item.stockId || 0, // adjust if you have real stockId
                    barcode: item.barcode,
                    batchNumber: item.batchNumber,
                    category: item.category || (isServiceItem(item) ? "services" : "products"),
                    quantity: item.quantity,

                    price: item.price,
                    total: item.total,

                    gstRate: isServiceItem(item) ? 0 : item.gstRate,

                    // ✅ USE STORED VALUES (NO RECALCULATION)
                    basePrice: isServiceItem(item) ? item.price : item.basePrice,
                    gstAmount: isServiceItem(item) ? 0 : item.gstPerUnit,
                    cgst: isServiceItem(item) ? 0 : item.cgstPerUnit,
                    sgst: isServiceItem(item) ? 0 : item.sgstPerUnit,

                    discountPercent: item.itemDiscount || 0,
                    discountAmount: discountAmountPerUnit,
                    finalValue: finalValuePerUnit
                };
            });

            // ✅ Totals
            const totalAmount = cart.reduce((sum, i) => sum + i.total, 0);

            const totalGst = taxableItems.reduce(
                (sum, i) => sum + (i.gstPerUnit * i.quantity),
                0
            );

            const totalCgst = taxableItems.reduce(
                (sum, i) => sum + (i.cgstPerUnit * i.quantity),
                0
            );

            const totalSgst = taxableItems.reduce(
                (sum, i) => sum + (i.sgstPerUnit * i.quantity),
                0
            );

            const totalDiscount = cart.reduce((sum, item) => {
                const discountPerUnit =
                    (item.price * (item.itemDiscount || 0)) / 100;
                return sum + (discountPerUnit * item.quantity);
            }, 0);

            // ✅ Final payload
            const payload = {
                receiptNo,
                totalAmount,
                totalGst,
                totalCgst,
                totalSgst,
                totalDiscount,
                items
            };

            console.log("Sending payload:", payload);

            // ✅ API call
            const res = await fetch("/v1/bills", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: "Bearer " + token
                },
                body: JSON.stringify(payload)
            });

            if (!res.ok) {
                let errorMessage = "Something went wrong";

                try {
                    const errorData = await res.json(); // ✅ try parsing JSON
                    errorMessage = errorData.message || errorMessage;
                } catch {
                    const text = await res.text(); // fallback if not JSON
                    errorMessage = text;
                }

                throw new Error(errorMessage);
            }

            const data = await res.json();

            setMessage("✅ Bill submitted successfully");
            setMessageType("success");

            // ✅ Reset UI after success
            setCart([]);
            setTotal(0);
            setShowPrint(false);
            setReceiptNo(generateReceiptNo());

        } catch (err) {
            console.error(err);
            setMessage("❌ Error submitting bill: " + err.message);
            setMessageType("error");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handlePrint = () => {
        setShowPrint(true);
    };

    // ✅ CORRECT GST TOTALS
    const totalCGST = taxableItems.reduce(
        (sum, item) => sum + (item.cgstPerUnit * item.quantity),
        0
    );

    const totalSGST = taxableItems.reduce(
        (sum, item) => sum + (item.sgstPerUnit * item.quantity),
        0
    );

    const printTaxableItems = cart.filter((item) => Number(item.gstRate || 0) > 0);
    const printExemptItems = cart.filter((item) => Number(item.gstRate || 0) === 0);
    const totalSavings = cart.reduce((sum, item) => {
        const discountPerUnit = (item.price * (item.itemDiscount || 0)) / 100;
        return sum + discountPerUnit * item.quantity;
    }, 0);

    const totalQuantity = cart.reduce(
        (sum, item) => sum + item.quantity,
        0
    );

    const taxableTotal = taxableItems.reduce(
        (sum, item) => sum + item.total,
        0
    );

    const serviceTotal = serviceItems.reduce(
        (sum, item) => sum + item.total,
        0
    );

    return (
        <>
            <div className="modal-backdrop fade show"></div>

            <div className="modal show d-block">
                <div className="modal-dialog modal-lg">
                    <div className="modal-content">

                        <div className="modal-header">
                            <h5>Billing</h5>
                            <button
                                className="btn-close"
                                onClick={onClose}
                            ></button>
                        </div>

                        {message && (
                            <div className={`alert alert-${messageType} alert-dismissible fade show`} role="alert">
                                {message}
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setMessage(null)}
                                ></button>
                            </div>
                        )}

                        <div className="modal-body">

                            {showAddToBillPrompt && (
                                <div className="alert alert-warning d-flex flex-column flex-sm-row align-items-sm-center justify-content-between gap-2 mb-3">
                                    <div>
                                        <strong>Product not found.</strong> Do you want to add it to bill?
                                    </div>
                                    <div className="d-flex gap-2">
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-primary px-2 py-1"
                                            onClick={handleAddUnknownProductToBill}
                                        >
                                            Add to bill
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-secondary"
                                            onClick={handleCancelUnknownProduct}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            )}

                            <div className="position-relative mb-3">
                                <input
                                    ref={barcodeRef}
                                    className="form-control"
                                    placeholder={useLabTestSearch ? "Search lab tests" : "Scan barcode or type to search products"}
                                    value={barcode}
                                    onChange={(e) => handleBarcodeChange(e.target.value)}
                                    onFocus={() => {
                                        if (searchSuggestions.length > 0) {
                                            setShowSuggestions(true);
                                        }
                                    }}
                                    onBlur={() => {
                                        window.setTimeout(() => setShowSuggestions(false), 150);
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            setShowSuggestions(false);
                                            if (useLabTestSearch) {
                                                if (searchSuggestions.length > 0) {
                                                    selectSuggestion(searchSuggestions[0]);
                                                }
                                            } else {
                                                fetchAndAddProduct();
                                            }
                                        }
                                    }}
                                    autoFocus
                                />
                                <div className="form-check mt-2">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="billingLabTestSearch"
                                        checked={useLabTestSearch}
                                        onChange={(e) => {
                                            const checked = e.target.checked;
                                            setUseLabTestSearch(checked);
                                            setBarcode("");
                                            setSearchSuggestions([]);
                                            setShowSuggestions(false);
                                            if (checked) {
                                                setLabTests([]);
                                                labTestsLoadedRef.current = false;
                                            }
                                        }}
                                    />
                                    <label className="form-check-label small" htmlFor="billingLabTestSearch">
                                        Search lab tests
                                    </label>
                                </div>

                                {showSuggestions && (
                                    <div
                                        className="list-group position-absolute w-100 shadow"
                                        style={{ zIndex: 25, top: "100%", left: 0, maxHeight: 240, overflowY: "auto" }}
                                    >
                                        {suggestionsLoading || labTestLoading ? (
                                            <div className="list-group-item py-2 text-muted">
                                                Searching...
                                            </div>
                                        ) : searchSuggestions.length > 0 ? (
                                            searchSuggestions.map((item, index) => {
                                                const label =
                                                    item?.productName ||
                                                    item?.profileParticulars ||
                                                    item?.testName ||
                                                    item?.name ||
                                                    item?.barcode ||
                                                    item?.code ||
                                                    `Result ${index + 1}`;
                                                const code =
                                                    item?.barcode ||
                                                    item?.code ||
                                                    item?.productCode ||
                                                    (useLabTestSearch ? `LABTEST-${item?.id || index + 1}` : "") ||
                                                    "";
                                                const price =
                                                    item?.price ??
                                                    item?.actualPrice ??
                                                    item?.offerPrice ??
                                                    item?.batches?.[0]?.price;

                                                return (
                                                    <button
                                                        key={`${code || label}-${index}`}
                                                        type="button"
                                                        className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                                                        onMouseDown={(e) => {
                                                            e.preventDefault();
                                                            selectSuggestion(item);
                                                        }}
                                                    >
                                                        <span className="text-start">
                                                            <div className="fw-semibold">{label}</div>
                                                            <small className="text-muted">
                                                                {useLabTestSearch
                                                                    ? `Lab test${code ? ` • ${code}` : ""}`
                                                                    : code
                                                                        ? `Code: ${code}`
                                                                        : "Click to add"}
                                                            </small>
                                                        </span>
                                                        <span className="ms-3 text-nowrap text-muted">
                                                            {price != null && price !== "" ? `₹${price}` : ""}
                                                        </span>
                                                    </button>
                                                );
                                            })
                                        ) : (
                                            <div className="list-group-item py-2 text-muted">
                                                No matching products
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* 🔹 Batch selection */}
                            {batches.length > 1 && (
                                <div className="mb-3 ms-auto p-2 border bg-light rounded-3" style={{ maxWidth: "360px" }}>
                                    <small className="d-block mb-2 fw-semibold text-end">Select Batch:</small>

                                    {batches.map((b, i) => (
                                        <div
                                            key={i}
                                            className="d-flex justify-content-between align-items-center mb-2 p-2 border bg-white rounded-2" style={{ fontSize: "0.85rem" }}
                                        >
                                            <div className="me-2">{b.batchNumber} | ₹{b.price} | Qty: {b.quantity}
                                            </div>

                                            <button
                                                className="btn btn-sm btn-primary px-2 py-1"
                                                onClick={() =>
                                                    addToCart(
                                                        b,
                                                        productName,
                                                        currentBarcode,
                                                        gstRate,
                                                        b.itemDiscount || 0
                                                    )
                                                }
                                            >
                                                Select
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* 🔹 Cart */}
                            {taxableItems.length > 0 && (
                                <>
                                    <h6 className="mt-3 mb-2">GST Items</h6>
                                    <table className="table table-sm text-center align-middle">
                                        <thead>
                                            <tr>
                                                <th>Item</th>
                                                <th>Barcode</th>
                                                <th>Qty</th>
                                                <th>Price</th>
                                                <th>Disc%</th>
                                                <th>Total</th>
                                                <th>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {taxableItems.map((item, i) => {
                                                const actualIndex = cart.findIndex(
                                                    (entry) =>
                                                        entry.barcode === item.barcode &&
                                                        entry.batchNumber === item.batchNumber
                                                );

                                                return (
                                                    <tr key={`tax-${item.batchNumber}-${item.barcode}-${i}`}>
                                                        <td style={{ verticalAlign: "middle" }}>{item.productName}</td>
                                                        <td style={{ verticalAlign: "middle" }}>{item.barcode}</td>
                                                        <td style={{ width: "80px" }}>
                                                            <input
                                                                type="number"
                                                                inputMode="numeric"
                                                                className="form-control form-control-sm text-center"
                                                                style={{ textAlign: "center" }}
                                                                value={item.quantity}
                                                                onChange={(e) => updateQuantity(actualIndex, e.target.value)}
                                                            />
                                                        </td>
                                                        <td style={{ width: "90px" }}>
                                                            <input
                                                                type="text"
                                                                className="form-control form-control-sm text-center"
                                                                style={{ textAlign: "center" }}
                                                                value={item.price}
                                                                onChange={(e) => updatePrice(actualIndex, e.target.value)}
                                                            />
                                                        </td>
                                                        <td style={{ width: "80px" }}>
                                                            <input
                                                                type="text"
                                                                className="form-control form-control-sm text-center"
                                                                style={{ textAlign: "center" }}
                                                                value={item.itemDiscount || 0}
                                                                onChange={(e) => updateDiscount(actualIndex, e.target.value)}
                                                            />
                                                        </td>
                                                        <td style={{ verticalAlign: "middle" }}>{item.total}</td>
                                                        <td style={{ verticalAlign: "middle" }}>
                                                            <button
                                                                className="btn btn-sm btn-danger"
                                                                onClick={() => removeItem(actualIndex)}
                                                            >
                                                                Delete
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </>
                            )}

                            {serviceItems.length > 0 && (
                                <>
                                    <h6 className="mt-3 mb-2">Services (No GST)</h6>
                                    <table className="table table-sm text-center align-middle">
                                        <thead>
                                            <tr>
                                                <th>Item</th>
                                                <th>Barcode</th>
                                                <th>Qty</th>
                                                <th>Price</th>
                                                <th>Disc%</th>
                                                <th>Total</th>
                                                <th>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {serviceItems.map((item, i) => {
                                                const actualIndex = cart.findIndex(
                                                    (entry) =>
                                                        entry.barcode === item.barcode &&
                                                        entry.batchNumber === item.batchNumber
                                                );

                                                return (
                                                    <tr key={`svc-${item.batchNumber}-${item.barcode}-${i}`}>
                                                        <td style={{ verticalAlign: "middle" }}>{item.productName}</td>
                                                        <td style={{ verticalAlign: "middle" }}>{item.barcode}</td>
                                                        <td style={{ width: "80px" }}>
                                                            <input
                                                                type="number"
                                                                inputMode="numeric"
                                                                className="form-control form-control-sm text-center"
                                                                style={{ textAlign: "center" }}
                                                                value={item.quantity}
                                                                onChange={(e) => updateQuantity(actualIndex, e.target.value)}
                                                            />
                                                        </td>
                                                        <td style={{ width: "90px" }}>
                                                            <input
                                                                type="text"
                                                                className="form-control form-control-sm text-center"
                                                                style={{ textAlign: "center" }}
                                                                value={item.price}
                                                                onChange={(e) => updatePrice(actualIndex, e.target.value)}
                                                            />
                                                        </td>
                                                        <td style={{ width: "80px" }}>
                                                            <input
                                                                type="text"
                                                                className="form-control form-control-sm text-center"
                                                                style={{ textAlign: "center" }}
                                                                value={item.itemDiscount || 0}
                                                                onChange={(e) => updateDiscount(actualIndex, e.target.value)}
                                                            />
                                                        </td>
                                                        <td style={{ verticalAlign: "middle" }}>{item.total}</td>
                                                        <td style={{ verticalAlign: "middle" }}>
                                                            <button
                                                                className="btn btn-sm btn-danger"
                                                                onClick={() => removeItem(actualIndex)}
                                                            >
                                                                Delete
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </>
                            )}

                            <small>Total Quantity: {totalQuantity}</small>
                            <h5>Total: ₹{Math.ceil(total)}</h5>
                            {serviceItems.length > 0 && (
                                <small className="d-block text-muted">
                                    Services total: ₹{Math.ceil(serviceTotal.toFixed(2))}
                                </small>
                            )}

                            {showManualEntry && (
                                <div className="mt-3 p-3 border bg-light rounded">
                                    <h6>Add Item Manually</h6>

                                    <div className="row g-2">

                                        <div className="col-6">
                                            <input
                                                className="form-control"
                                                placeholder="Product Name"
                                                name="name"
                                                value={manualItem.name}
                                                onChange={(e) =>
                                                    setManualItem({ ...manualItem, name: e.target.value })
                                                }
                                            />
                                        </div>

                                        <div className="col-6">
                                            <input
                                                className="form-control"
                                                placeholder="Barcode"
                                                name="barcode"
                                                value={manualItem.barcode}
                                                onChange={(e) =>
                                                    setManualItem({ ...manualItem, barcode: normalizeBarcode(e.target.value) })
                                                }
                                            />
                                        </div>

                                        <div className="col-4">
                                            <input
                                                className="form-control"
                                                type="number"
                                                placeholder="Qty"
                                                value={manualItem.quantity}
                                                onChange={(e) =>
                                                    setManualItem({ ...manualItem, quantity: e.target.value })
                                                }
                                            />
                                        </div>

                                        <div className="col-4">
                                            <input
                                                className="form-control"
                                                type="number"
                                                placeholder="Discount %"
                                                value={manualItem.discount}
                                                onChange={(e) =>
                                                    setManualItem({ ...manualItem, discount: e.target.value })
                                                }
                                            />
                                        </div>

                                        <div className="col-4">
                                            <input
                                                className="form-control"
                                                type="number"
                                                placeholder="Price"
                                                value={manualItem.price}
                                                onChange={(e) =>
                                                    setManualItem({ ...manualItem, price: e.target.value })
                                                }
                                            />
                                        </div>

                                        <div className="col-4">
                                            <input
                                                className="form-control"
                                                type="number"
                                                placeholder="GST %"
                                                value={manualItem.gst}
                                                onChange={(e) =>
                                                    setManualItem({ ...manualItem, gst: e.target.value })
                                                }
                                            />
                                        </div>

                                    </div>

                                    <div className="mt-3 d-flex justify-content-end gap-2">
                                        <button
                                            className="btn btn-secondary"
                                            onClick={() => setShowManualEntry(false)}
                                        >
                                            Cancel
                                        </button>

                                        <button
                                            className="btn btn-primary"
                                            onClick={handleAddManualItem}
                                        >
                                            Add to Bill
                                        </button>
                                    </div>
                                </div>
                            )}

                        </div>

                        <div className="modal-footer">
                            <button className="btn btn-secondary" onClick={onClose}>
                                Close
                            </button>

                            <button className="btn btn-success" disabled={isSubmitting} onClick={handleSubmit}>
                                Submit
                            </button>

                            <button
                                className="btn btn-primary"
                                onClick={handlePrint}
                                disabled={cart.length === 0}
                            >
                                Print
                            </button>
                        </div>

                    </div>
                </div>
            </div>

            {/* 🧾 PRINT PREVIEW */}
            {showPrint && (

               
                <>  
                    <div className="print-only">

                        <div className="modal-backdrop fade show"></div>

                        <div className="modal show d-block">
                            <div className="modal-dialog">
                                <div className="modal-content">

                                    <div className="modal-header  no-print">
                                        <h5>Print Preview</h5>
                                        <button
                                            className="btn-close"
                                            onClick={() => setShowPrint(false)}
                                        ></button>
                                    </div>

                                    <div id="print-section" className="modal-body">

                                        {clinic?.logoUrl && (
                                            <div style={{ textAlign: "center", marginBottom: "8px" }}>
                                                <img
                                                    className="receipt-logo"
                                                    src={clinic.logoUrl}
                                                    alt="Clinic Logo"
                                                    style={{
                                                        objectFit: "contain"
                                                    }}
                                                />
                                            </div>
                                        )}


                                        {clinic?.gstn && (
                                            <small>GSTIN: {clinic.gstn}</small>
                                        )}
                                        <div className="text-center mb-2">
                                            <h5>{clinic?.name || clinicName}</h5>

                                            {clinic?.gstn && (
                                                <small>GSTIN: {clinic.gstn}</small>
                                            )}
                                        </div>

                                        <div className="text-center mb-2">
                                            <small>
                                                {clinic?.address1 && <>{clinic.address1}<br /></>}
                                                {clinic?.address2 && <>{clinic.address2}<br /></>}
                                                {clinic?.city && clinic?.state && (
                                                    <>
                                                        {clinic.city}, {clinic.state}<br />
                                                    </>
                                                )}
                                                {clinic?.pin && <>PIN: {clinic.pin}</>}
                                            </small>
                                        </div>

                                        <hr style={{ borderTop: "1px dotted black" }} />

                                        <div className="text-center mb-2">
                                            <small>TAX INVOICE</small>
                                            <div className="text-center mb-1">
                                                <small>Receipt No: {receiptNo}</small>
                                            </div>
                                        </div>

                                        {(printTaxableItems.length > 0 || printExemptItems.length > 0) && (
                                        <>
                                        <hr style={{ borderTop: "1px dotted black" }} />

                                        <div style={{ fontSize: "11px" }}>
                                            {printTaxableItems.length > 0 && (
                                            <table style={{ width: "100%", tableLayout: "fixed", fontSize: "11px" }}>
                                                <thead>
                                                    <tr>
                                                        <th style={{ width: "6%" }}>#</th>
                                                        <th style={{ width: "44%" }}>Item</th>
                                                        <th style={{ width: "12%" }}>Qty</th>
                                                        <th style={{ width: "18%" }}>MRP</th>
                                                        <th style={{ width: "20%", textAlign: "right" }}>Amt</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {printTaxableItems.map((item, i) => (
                                                        <tr key={`tax-${item.batchNumber}-${item.barcode}-${i}`}>
                                                            <td>{i + 1}</td>
                                                            <td style={{ wordBreak: "break-word" }}>
                                                                {item.productName}
                                                            </td>
                                                            <td>{item.quantity}</td>
                                                            <td>{item.price.toFixed(2)}</td>
                                                            <td style={{ textAlign: "right" }}>{item.total.toFixed(2)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                            )}

                                            {printTaxableItems.length > 0 && printExemptItems.length > 0 && (
                                            <hr style={{ borderTop: "1px dotted black" }} />
                                            )}

                                            {printExemptItems.length > 0 && (
                                            <table style={{ width: "100%", tableLayout: "fixed", fontSize: "11px" }}>
                                                <thead>
                                                    <tr>
                                                        <th style={{ width: "6%" }}>#</th>
                                                        <th style={{ width: "44%" }}>Item</th>
                                                        <th style={{ width: "12%" }}>Qty</th>
                                                        <th style={{ width: "18%" }}>MRP</th>
                                                        <th style={{ width: "20%", textAlign: "right" }}>Amt</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {printExemptItems.map((item, i) => (
                                                        <tr key={`ex-${item.batchNumber}-${item.barcode}-${i}`}>
                                                            <td>{i + 1}</td>
                                                            <td style={{ wordBreak: "break-word" }}>
                                                                {item.productName}
                                                                {Number(item.gstRate || 0) === 0 ? "" : ""}
                                                            </td>
                                                            <td>{item.quantity}</td>
                                                            <td>{item.price.toFixed(2)}</td>
                                                            <td style={{ textAlign: "right" }}>{item.total.toFixed(2)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                            )}
                                        </div>

                                        <hr />
                                        </>
                                        )}

                                        <small>
                                            CGST: ₹{totalCGST.toFixed(2)} | SGST: ₹{totalSGST.toFixed(2)}
                                        </small>

                                        <div style={{ fontFamily: "monospace", fontSize: "11px" }}>
                                            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "16px", fontWeight: 700, marginTop: "4px" }}>
                                                <span>Total</span>
                                                <span style={{ textAlign: "right" }}>{Math.ceil(total).toFixed(2)}</span>
                                            </div>

                                             
                                        </div>

                                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: "11px", fontWeight: 700, marginBottom: "8px" }}>
                                            <span>Total Savings</span>
                                            <span style={{ textAlign: "right" }}>₹{totalSavings.toFixed(2)}</span>
                                        </div>

                                        <hr />

                                        <div style={{ fontSize: "11px", marginTop: "8px", lineHeight: "1.4" }}>
                                            <strong>Notes:</strong>
                                            <ol style={{ paddingLeft: "16px", margin: "4px 0" }}>
                                                <li>All prices are inclusive of GST unless mentioned otherwise.</li>
                                                <li>GST has been calculated as per applicable rates.</li>
                                                <li>CGST and SGST have been applied equally as per government norms.</li>
                                                <li>This is a computer-generated invoice and does not require a signature.</li>
                                                <li>Goods once sold will not be taken back or exchanged.</li>
                                                <li>Please retain this receipt for future reference.</li>
                                                <li>In case of any discrepancy, contact the clinic within 24 hours.</li>
                                            </ol>
                                        </div>

                                        <div className="text-center">
                                            <small>Thank you! Visit Again 🐾</small>
                                        </div>

                                    </div>

                                    <div className="modal-footer no-print">
                                        <button
                                            className="btn btn-secondary"
                                            onClick={() => setShowPrint(false)}
                                        >
                                            Close
                                        </button>

                                        <button className="btn btn-success" onClick={handlePrintNow}>
                                            Print Now
                                        </button>
                                    </div>

                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </>
    );
}

export default BillingModal;
